import { useEffect, useState, useMemo } from 'react';
import { TopBar } from '@/components/layout/TopBar';
import { supabase } from '@/integrations/supabase/client';
import { TicketCard } from '@/components/tickets/TicketCard';
import {
  buildSplitPartLabelDataMap,
  type SplitGroupTicketShape,
} from '@/utils/splitGroupIdentity';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Trash2, RotateCcw, Archive, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { formatShopTime } from '@/utils/formatShopTime';
import { isVehiclePresent } from '@/utils/deriveVehiclePresence';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import {
  restoreDeletedTicket,
  didRestore,
  describeRestoreRefusal,
  needsRestoreReload,
} from '@/utils/ticketRestoreLifecycle';
// DTC-L: route ticket archive/restore lifecycle write timestamps
// through the canonical clock so persisted ticket-mutation writes are
// deterministic under `clock.setFixed(...)`. Same drift class as
// DTC-K's OptiboardTicketRow migration.
import { clock } from '@/utils/clock';

const DeletedTickets = () => {
  const [tickets, setTickets] = useState<any[]>([]);
  // Phase 7.3: board-level split label data map. Built ONCE per
  // `tickets` change and passed per-card so SplitLink can render real
  // "Part X of Y" labels without per-card reconstruction.
  const splitLabelDataByTicketId = useMemo(
    () => buildSplitPartLabelDataMap(tickets as unknown as SplitGroupTicketShape[]),
    [tickets],
  );
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [ticketToArchive, setTicketToArchive] = useState<string | null>(null);
  const [showArchiveAll, setShowArchiveAll] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const { toast } = useToast();
  const { activeShop, user } = useAuth();
  const { shopTimezone } = useShopTimezone();

  useEffect(() => {
    loadData();

    // Real-time subscription for ticket deletions/restorations only.
    //
    // Chronomancer R-RT-4: scope channel name with shop_id. Previously the
    // unscoped name `'deleted-tickets-changes'` collided across tabs from
    // different shops in the same browser (Supabase multiplexes duplicates
    // correctly but the collision wastes slots and hides shop-scope intent
    // from anyone grepping channel names). Matches the scoping used by
    // every other realtime site in this audit.
    //
    // Chronomancer R-RT-6: surface channel status drops so a stale list is
    // diagnosable. Without the status callback, a TIMED_OUT here silently
    // stops refreshing deletions until a manual reload.
    const channel = supabase
      .channel(`deleted-tickets-changes-${activeShop?.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tickets',
          filter: `shop_id=eq.${activeShop?.id}`
        },
        (payload) => {
          console.log('Deleted tickets page - ticket update:', payload);
          // Only reload if deleted_at changed (ticket was deleted or restored)
          const oldDeleted = payload.old?.deleted_at;
          const newDeleted = payload.new?.deleted_at;
          if (oldDeleted !== newDeleted) {
            console.log('Ticket deletion status changed, reloading...');
            loadData();
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[DeletedTickets] realtime channel status:', status, err);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeShop?.id]);

  const loadData = async () => {
    if (!activeShop) return;
    
    try {
      const [ticketsRes, techsRes] = await Promise.all([
        supabase
          .from('tickets')
          .select(`
            *,
            vehicle:vehicles(id, year, make, model, engine_size),
            ticket_jobs(minutes_override, notes, job:jobs(*))
          `)
          .eq('shop_id', activeShop.id)
          .not('deleted_at', 'is', null)
          .is('archived_at', null) // Only show non-archived deleted tickets
          .order('deleted_at', { ascending: false })
          // IV-D-28 R1 FIX-2: cap at 500 most-recent deleted. Each ticket row
          // renders as a full TicketCard with nested joins; shops that accumulate
          // many deleted-but-not-archived tickets previously fetched and
          // rendered them all. 500 is a generous ceiling — beyond that the user
          // should archive older deletions to reduce the working set.
          .limit(500),
        supabase.from('technicians').select('*').eq('shop_id', activeShop.id).eq('employment_status', 'Active')
      ]);

      console.log('DeletedTickets - ticketsRes:', ticketsRes);

      if (ticketsRes.error) {
        console.error('Error fetching tickets:', ticketsRes.error);
        toast({
          title: "Error loading deleted tickets",
          description: ticketsRes.error.message,
          variant: "destructive"
        });
        setIsLoading(false);
        return;
      }

      if (techsRes.error) {
        console.error('Error fetching technicians:', techsRes.error);
        toast({
          title: "Error loading technicians",
          description: techsRes.error.message,
          variant: "destructive"
        });
      }

      if (ticketsRes.data) {
        // Collect unique user IDs who deleted tickets
        const deletedByIds = [...new Set(
          ticketsRes.data
            .map(t => t.deleted_by)
            .filter(Boolean)
        )] as string[];

        // Fetch profiles for deleted_by users
        let profilesMap: Record<string, { display_name?: string; email?: string }> = {};
        if (deletedByIds.length > 0) {
          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, display_name, email')
            .in('id', deletedByIds);

          if (profilesError) {
            console.error('Error fetching profiles:', profilesError);
          } else if (profiles) {
            profilesMap = profiles.reduce((acc, p) => ({
              ...acc,
              [p.id]: { display_name: p.display_name, email: p.email }
            }), {});
          }
        }

        const transformed = ticketsRes.data.map(t => {
          const profile = t.deleted_by ? profilesMap[t.deleted_by] : null;
          const deletedByName = profile?.display_name || profile?.email || 'Unknown';
          
          return {
            ...t,
            jobs: t.ticket_jobs?.map((tj: any) => ({
              ...tj.job,
              standardMinutes: tj.minutes_override || tj.job.standard_minutes,
              notes: tj.notes
            })) || [],
            vehiclePresent: isVehiclePresent(t),
            predictedMinutes: t.predicted_minutes,
            assignedTechId: t.assigned_tech_id,
            pauseReason: t.pause_reason,
            startTime: formatShopTime(t.start_time, shopTimezone, 'short'),
            deletedBy: deletedByName,
            deletedAt: t.deleted_at,
            deletionReason: t.deletion_reason
          };
        });
        setTickets(transformed);
      }
      if (techsRes.data) setTechnicians(techsRes.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading deleted tickets:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while loading deleted tickets",
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  const handleRestore = async (ticketId: string) => {
    // 8G.2C.3: routed through the SERVER restore authority.
    //
    // The legacy `restoreFromDeletedToParkingLot` transition wrote
    // status='Paused', timer_status='paused', paused_at and paused_by='user' —
    // manufacturing a technician pause for a person who never paused — and
    // assumed a Deleted row already had its sessions closed, which stopped
    // being true when 8G.2 removed the forced close from deletion.
    //
    // The authority refuses contradictory timer evidence and returns the
    // ticket as ACTIVE and UNASSIGNED in the general lot.
    const restored = await restoreDeletedTicket(ticketId, 'Restored from Deleted Tickets');

    // 8G.2C.4 §16/§17: an unconfirmed result is NOT a refusal and NOT a
    // success. The transaction may have committed, so do not re-fire the human
    // Restore act; the canonical attribution is missing, so do not emit a
    // downstream record built from it either. Reload and let the operator see
    // the real state.
    if (needsRestoreReload(restored.outcome)) {
      toast({
        title: 'Restore result unconfirmed',
        description: describeRestoreRefusal(restored),
        variant: 'destructive',
      });
      await loadData();
      return;
    }

    if (!didRestore(restored.outcome)) {
      toast({
        title: restored.outcome === 'TERMINAL_EVIDENCE_CONFLICT'
          ? 'Unresolved technician work'
          : 'Cannot restore',
        description: describeRestoreRefusal(restored),
        variant: 'destructive',
      });
    } else {
      // Log pensieve event
      // Canonical shop from the server, resolved under the transition lock —
      // not the React context, which may be stale or a different shop.
      const restoreShopId = restored.shopId;
      if (restoreShopId && user?.id) {
        await supabase.from('pensieve_events').insert({
          shop_id: restoreShopId,
          ticket_id: ticketId,
          event_type: 'ticket.restored',
          session_id: crypto.randomUUID(),
          timestamp: clock.now().toISOString(),
          metadata: {
            restored_to: 'parking_lot',
            // ACTUAL final status, not a hardcoded assumption.
            restored_to_status: restored.status,
            restored_from_status: restored.restoredFrom,
            released_assigned_tech_id: restored.releasedAssignedTechId,
            performed_by_user_id: user.id
          }
        });

        // OCIS-R69-FIX (HIGH-2): previously restore wrote Pensieve only —
        // the restored ticket silently re-emerged with no timeline event.
        // Emit ticket_status_changed so the audit trail records the restore.
        logTicketEventToOcis({
          ticketId,
          shopId: restoreShopId,
          eventType: 'ticket_status_changed',
          content: 'Ticket restored from deleted → general lot (active, unassigned)',
          authorUserId: user.id,
          payload: {
            fromStatus: 'Deleted',
            // ACTUAL final status. The old copy hardcoded 'Paused' because the
            // legacy transition fabricated one; nobody paused.
            toStatus: restored.status ?? 'Active',
            reason: 'restored_from_deleted',
            restored_to: 'parking_lot',
          },
        }).catch(err => console.error('[DeletedTickets] Restore OCIS log error:', err));
      }

      toast({
        title: 'Ticket Restored',
        description: 'Ticket moved to parking lot',
      });
    }
  };

  // Core-100 R4 wave 2 (CORE-AS): archiving goes through ONE server authority, archive_deleted_tickets
  // (20270206022500). It checks the caller manages the ticket's shop, archives only DELETED tickets, and records the
  // act itself; a raw browser UPDATE of tickets.archived_at is refused (TICKET_ARCHIVE_THROUGH_AUTHORITY).
  const archiveThroughAuthority = async (ticketIds: string[]) => {
    const { data, error } = await supabase.rpc('archive_deleted_tickets' as never, { p_ticket_ids: ticketIds } as never);
    if (error) return { error, archived: [] as string[], notArchived: ticketIds };
    const r = (data ?? {}) as { archived?: string[]; not_archived?: string[] };
    return { error: null, archived: r.archived ?? [], notArchived: r.not_archived ?? [] };
  };

  const handleArchive = async () => {
    if (!ticketToArchive) return;

    const { error, archived } = await archiveThroughAuthority([ticketToArchive]);

    if (error || archived.length === 0) {
      toast({
        title: 'Error',
        description: 'Failed to archive ticket',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Ticket Archived',
        description: `Ticket ${ticketToArchive} has been archived`,
      });
      loadData();
    }

    setTicketToArchive(null);
  };

  const handleArchiveAll = async () => {
    if (confirmText !== 'ARCHIVE') {
      toast({
        title: 'Confirmation Required',
        description: 'Please type ARCHIVE to confirm',
        variant: 'destructive',
      });
      return;
    }

    const ticketIds = tickets.map(t => t.id);
    
    if (ticketIds.length === 0) return;

    const { error, archived, notArchived } = await archiveThroughAuthority(ticketIds);

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to archive tickets',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'All Tickets Archived',
        description: `${archived.length} ticket${archived.length === 1 ? '' : 's'} archived`
          + (notArchived.length > 0 ? `; ${notArchived.length} could not be archived` : ''),
      });
      loadData();
    }

    setShowArchiveAll(false);
    setConfirmText('');
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <TopBar />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <TopBar />
      
      <main className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Deleted Tickets</h1>
              <p className="text-muted-foreground">
                Restore tickets or archive them permanently
              </p>
            </div>
            {tickets.length > 0 && (
              <Button
                variant="destructive"
                onClick={() => setShowArchiveAll(true)}
              >
                <Archive className="h-4 w-4 mr-2" />
                Archive All ({tickets.length})
              </Button>
            )}
          </div>

          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No deleted tickets</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {tickets.map(ticket => (
                <div key={ticket.id} className="space-y-2">
                  <div className="relative">
                    <TicketCard 
                      ticket={{
                        ...ticket,
                        assignedTechId: ticket.assigned_tech_id,
                        customerName: ticket.customer_name,
                        customerPhone: ticket.customer_phone,
                        dueOut: ticket.due_out,
                        isComeback: ticket.is_comeback,
                        startTime: formatShopTime(ticket.start_time, shopTimezone, 'short'),
                        predictedMinutes: ticket.predicted_minutes,
                        pauseReason: ticket.pause_reason,
                        actualMinutes: ticket.actual_minutes
                      }}
                      technicians={technicians.map(t => ({
                        id: t.id,
                        name: t.name,
                        is_present: t.is_present
                      }))}
                      splitLabelData={splitLabelDataByTicketId.get(ticket.id) ?? null}
                      hideTimerControls={true}
                      hideKebabMenu={true}
                    />
                    <div className="mt-2 flex gap-2">
                      {/* 8G.2C.3 §9: this page lists everything carrying a
                          deletion tombstone, and since 8G.2B a CANCELLED visit
                          carries one too — so cancelled tickets appear here.
                          They cannot be restored: cancellation releases the
                          chain lineage, so bringing one back would produce an
                          orphan standalone ticket rather than the visit it was,
                          and `restore_ticket_if_quiescent` refuses it with
                          UNSUPPORTED_TERMINAL_STATE. Offering a button whose
                          only possible answer is "no" is a lie about what the
                          page can do, so say what is true instead. */}
                      {ticket.status === 'Cancelled' ? (
                        <p className="flex-1 self-center text-xs text-muted-foreground">
                          Cancelled visits can’t be restored — create a new ticket.
                        </p>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleRestore(ticket.id)}
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Restore
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setTicketToArchive(ticket.id)}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {(ticket.deletionReason || ticket.deletedBy || ticket.deletedAt) && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-sm space-y-1">
                      {ticket.deletionReason && (
                        <p className="font-medium text-destructive">
                          Reason: {ticket.deletionReason}
                        </p>
                      )}
                      {(ticket.deletedBy || ticket.deletedAt) && (
                        <p className="text-muted-foreground">
                          {ticket.deletedBy && <>Deleted by <span className="font-medium">{ticket.deletedBy}</span></>}
                          {ticket.deletedAt && (
                            <>
                              {ticket.deletedBy && ' '}
                              at {new Date(ticket.deletedAt).toLocaleString('en-US', {
                                month: '2-digit',
                                day: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </>
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Single ticket archive dialog */}
      <AlertDialog open={!!ticketToArchive} onOpenChange={() => setTicketToArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive the ticket. Archived tickets are hidden from all views
              but preserved in the database for record-keeping.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} className="bg-destructive text-destructive-foreground">
              <Archive className="h-4 w-4 mr-2" />
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive all dialog with confirmation */}
      <AlertDialog open={showArchiveAll} onOpenChange={(open) => {
        setShowArchiveAll(open);
        if (!open) setConfirmText('');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Archive All Deleted Tickets?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                This will archive <strong>{tickets.length}</strong> deleted ticket{tickets.length > 1 ? 's' : ''}:
              </p>
              <div className="max-h-32 overflow-y-auto bg-muted/50 rounded-md p-2 text-xs space-y-1">
                {tickets.slice(0, 10).map(t => (
                  <div key={t.id} className="flex justify-between">
                    <span className="font-mono">{t.id}</span>
                    <span className="text-muted-foreground">{t.customer_name || 'No name'}</span>
                  </div>
                ))}
                {tickets.length > 10 && (
                  <p className="text-muted-foreground text-center pt-1">
                    ...and {tickets.length - 10} more
                  </p>
                )}
              </div>
              <p className="text-sm">
                Archived tickets are hidden from all views but preserved in the database.
              </p>
              <div className="pt-2">
                <Label htmlFor="confirm-archive">
                  Type <strong>ARCHIVE</strong> to confirm:
                </Label>
                <Input
                  id="confirm-archive"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder="ARCHIVE"
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleArchiveAll} 
              disabled={confirmText !== 'ARCHIVE'}
              className="bg-destructive text-destructive-foreground"
            >
              <Archive className="h-4 w-4 mr-2" />
              Archive All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DeletedTickets;